'use client'

import {useState} from 'react'
import {ActivityDescription} from './ActivityDescription'
import {Check, ChevronDown} from 'lucide-react'
import {SelectionDropdown} from '../../ui/Dropdown/Dropdown'
import {cn} from '../../../lib/utils'

export type PrivateBookingOfferSelectorItem = {
    badge?: string | null
    description?: string | null
    details: string
    id: string
    name: string
}

type PrivateBookingOfferSelectorProps = {
    ariaLabel: string
    idPrefix: string
    items: PrivateBookingOfferSelectorItem[]
    onSelect: (offerId: string) => void
    placeholder: string
    selectedOfferId?: string | null
    variant?: 'app' | 'public'
}

export function PrivateBookingOfferSelector({
    ariaLabel,
    idPrefix,
    items,
    onSelect,
    placeholder,
    selectedOfferId,
    variant = 'app',
}: PrivateBookingOfferSelectorProps) {
    const isPublic = variant === 'public'
    const [open, setOpen] = useState(false)
    const selectedItem = items.find((item) => item.id === selectedOfferId) ?? null

    if (!items.length) return null

    return (
        <div className="min-w-0">
            <SelectionDropdown
                open={open}
                onOpenChange={setOpen}
                matchTriggerWidth
                contentRole="dialog"
                contentAriaLabel={ariaLabel}
                className="max-h-[280px] !max-w-none overflow-y-auto p-1 [scrollbar-width:thin]"
                trigger={(
                    <button
                        id={`${idPrefix}-offer-trigger`}
                        type="button"
                        aria-label={ariaLabel}
                        className={cn(
                            'flex min-h-14 w-full items-center justify-between gap-3 rounded-xl border px-3.5 py-2 text-left outline-hidden transition-[border-color,background-color,box-shadow] focus:ring-2',
                            isPublic
                                ? 'border-[var(--landing-neutral-100)] bg-white focus:border-[var(--landing-focus-ring)] focus:ring-[var(--landing-primary-100)]'
                                : 'border-gray-200 bg-white focus:border-primary-500 focus:ring-primary-100 dark:border-gray-700 dark:bg-gray-900 dark:focus:ring-primary-900'
                        )}
                        data-testid={`${idPrefix}-offer-trigger`}
                    >
                        <span className={cn(
                            'min-w-0 flex-1',
                            selectedItem && 'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3'
                        )}>
                            <span className="min-w-0">
                                <span className={cn(
                                    'block truncate text-sm font-semibold',
                                    isPublic ? 'text-[var(--landing-neutral-900)]' : 'text-gray-950 dark:text-gray-50'
                                )}>
                                    {selectedItem?.name ?? placeholder}
                                </span>
                                {selectedItem ? (
                                    <span className={cn(
                                        'mt-0.5 block truncate text-xs',
                                        isPublic ? 'text-[var(--landing-neutral-500)]' : 'text-gray-500 dark:text-gray-400'
                                    )}>
                                        {selectedItem.details}
                                    </span>
                                ) : null}
                            </span>
                            {selectedItem?.badge ? (
                                <span className={cn(
                                    'block max-w-40 truncate whitespace-nowrap text-xs font-medium',
                                    isPublic ? 'text-[var(--landing-primary-700)]' : 'text-emerald-700 dark:text-emerald-300'
                                )}>
                                    {selectedItem.badge}
                                </span>
                            ) : null}
                        </span>
                        <ChevronDown className={cn(
                            'h-4 w-4 shrink-0 transition-transform duration-normal ease-out motion-reduce:transition-none',
                            open && 'rotate-180',
                            isPublic ? 'text-[var(--landing-primary-600)]' : 'text-gray-500 dark:text-gray-400'
                        )} aria-hidden="true"/>
                    </button>
                )}
            >
                <div role="radiogroup" aria-label={ariaLabel} data-testid={`${idPrefix}-offer-list`}>
                    {items.map((item) => {
                        const isSelected = item.id === selectedOfferId
                        return (
                            <button
                                key={item.id}
                                type="button"
                                role="radio"
                                aria-checked={isSelected}
                                onClick={() => {
                                    onSelect(item.id)
                                    setOpen(false)
                                }}
                                className={cn(
                                    'flex min-h-14 w-full items-center gap-3 rounded-lg px-3 py-2 text-left outline-hidden transition-colors focus-visible:ring-2',
                                    isPublic
                                        ? isSelected
                                            ? 'bg-[rgba(237,240,253,0.72)] focus-visible:ring-[var(--landing-primary-100)]'
                                            : 'hover:bg-[var(--landing-neutral-50)] focus-visible:ring-[var(--landing-primary-100)]'
                                        : isSelected
                                            ? 'bg-primary-50 focus-visible:ring-primary-100 dark:bg-primary-950/35 dark:focus-visible:ring-primary-900'
                                            : 'hover:bg-gray-50 focus-visible:ring-primary-100 dark:hover:bg-gray-800 dark:focus-visible:ring-primary-900'
                                )}
                                data-selected={isSelected ? 'true' : 'false'}
                            >
                                <span className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3">
                                    <span className="min-w-0">
                                        <span className={cn(
                                            'block truncate text-sm font-semibold',
                                            isPublic ? 'text-[var(--landing-neutral-900)]' : 'text-gray-950 dark:text-gray-50'
                                        )}>
                                            {item.name}
                                        </span>
                                        <span className={cn(
                                            'mt-0.5 block truncate text-xs',
                                            isPublic ? 'text-[var(--landing-neutral-500)]' : 'text-gray-500 dark:text-gray-400'
                                        )}>
                                            {item.details}
                                        </span>
                                    </span>
                                    {item.badge ? (
                                        <span className={cn(
                                            'block max-w-40 truncate whitespace-nowrap text-xs font-medium',
                                            isPublic ? 'text-[var(--landing-primary-700)]' : 'text-emerald-700 dark:text-emerald-300'
                                        )}>
                                            {item.badge}
                                        </span>
                                    ) : null}
                                </span>
                                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                                    {isSelected ? (
                                        <Check className={cn(
                                            'h-4 w-4',
                                            isPublic ? 'text-[var(--landing-primary-600)]' : 'text-primary-600 dark:text-primary-300'
                                        )} aria-hidden="true"/>
                                    ) : null}
                                </span>
                            </button>
                        )
                    })}
                </div>
            </SelectionDropdown>
            {/* The description's own inset lines it up with the text of the field above. */}
            {selectedItem?.description?.trim() ? (
                <div className="mt-2"><ActivityDescription key={selectedItem.id} description={selectedItem.description}/></div>
            ) : null}
        </div>
    )
}
